import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { StarRating } from "../components/StarRating";
import { ContactTransporterDialog } from "../components/ContactTransporterDialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { REVIEW_CRITERIA, waLink } from "../lib/constants";
import { ShieldCheck, Truck, MapPin, CheckCircle2, Award, MessageSquare } from "lucide-react";

export const ReviewsList = ({ reviews }) => (
  <div className="space-y-3" data-testid="reviews-list">
    {reviews.length === 0 ? (
      <p className="text-sm text-muted-foreground">No reviews yet.</p>
    ) : reviews.map((r) => (
      <div key={r.review_id} className="border border-border p-4" data-testid={`review-${r.review_id}`}>
        <div className="flex items-center justify-between">
          <span className="font-medium">{r.company_name}</span>
          <StarRating value={r.overall} size="h-4 w-4" />
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{r.job_title} · {new Date(r.created_at).toLocaleDateString()}</p>
        {r.comment && <p className="text-sm mt-2">{r.comment}</p>}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
          {REVIEW_CRITERIA.filter((c) => c.key !== "overall").map((c) => (
            <span key={c.key}>{c.label}: <strong className="text-foreground">{r[c.key]}/5</strong></span>
          ))}
        </div>
        {r.reply && (
          <div className="mt-3 border-l-2 border-primary pl-3 bg-muted p-2">
            <span className="label-caps text-primary">Transporter reply</span>
            <p className="text-sm mt-1">{r.reply}</p>
          </div>
        )}
      </div>
    ))}
  </div>
);

export default function TransporterProfile() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/transporters/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto p-16 text-center font-display text-xl">Transporter not found</div></div>;
  if (!p) return <div className="min-h-screen bg-white"><Navbar /><div className="max-w-3xl mx-auto p-16 animate-pulse">Loading…</div></div>;

  const wa = p.whatsapp ? waLink(p.whatsapp, `Hi ${p.company_name || p.name}, I found you on WILTOM TradeLink.`) : null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="border-b border-foreground bg-foreground text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
          <span className="label-caps text-accent">Transporter profile</span>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <h1 className="font-display font-black text-3xl tracking-tight">{p.company_name || p.name}</h1>
            {p.featured && (
              <Badge className="rounded-none bg-accent text-foreground uppercase text-[10px] tracking-wider" data-testid="featured-badge">
                <Award className="h-3.5 w-3.5 mr-1" /> Featured
              </Badge>
            )}
            {p.documents_verified && (
              <Badge className="rounded-none bg-accent text-foreground uppercase text-[10px] tracking-wider" data-testid="verified-badge">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verified
              </Badge>
            )}
          </div>
          {p.bio && <p className="text-white/70 mt-2 max-w-2xl text-sm">{p.bio}</p>}
          <div className="flex items-center gap-3 mt-3">
            <StarRating value={p.average_rating || 0} />
            <span className="text-white/80 text-sm" data-testid="profile-rating">{p.average_rating ? `${p.average_rating} / 5` : "No rating yet"} · {p.reviews_count} reviews</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <aside className="lg:col-span-1 space-y-4">
          <div className="border border-border p-5">
            <div className="grid grid-cols-2 gap-4">
              <div><div className="font-display font-black text-2xl" data-testid="completed-jobs">{p.completed_jobs}</div><div className="label-caps text-muted-foreground">Completed jobs</div></div>
              <div><div className="font-display font-black text-2xl">{p.reviews_count}</div><div className="label-caps text-muted-foreground">Reviews</div></div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              {p.vehicle_type && <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> {p.vehicle_type}</div>}
              {(p.service_areas || p.country) && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> {[p.service_areas, p.country].filter(Boolean).join(" · ")}</div>}
              {p.routes && <div className="text-muted-foreground">Routes: {p.routes}</div>}
            </div>
            {p.job_capabilities?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3" data-testid="profile-capabilities">
                {p.job_capabilities.map((c) => <span key={c} className="text-[10px] uppercase tracking-wider bg-muted px-1.5 py-0.5">{c}</span>)}
              </div>
            )}
            <ContactTransporterDialog transporterId={p.transporter_id} transporterName={p.company_name || p.name}
              trigger={<Button className="w-full rounded-none mt-4 bg-primary hover:bg-primary/90" data-testid="profile-request-quote"><MessageSquare className="h-4 w-4 mr-1" /> Request quote through platform</Button>} />
            {wa && (
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="profile-whatsapp">
                <Button variant="outline" className="w-full rounded-none mt-2 border-[#25D366] text-[#1da851] hover:bg-[#25D366] hover:text-white">Contact on WhatsApp</Button>
              </a>
            )}
          </div>
          {p.average_rating && (
            <div className="border border-border p-5">
              <span className="label-caps text-muted-foreground">Rating breakdown</span>
              <div className="space-y-2 mt-3">
                {REVIEW_CRITERIA.map((c) => p.averages[c.key] != null && (
                  <div key={c.key} className="flex items-center justify-between text-sm">
                    <span>{c.label}</span>
                    <div className="flex items-center gap-2"><StarRating value={p.averages[c.key]} size="h-3.5 w-3.5" /><span className="text-muted-foreground w-8 text-right">{p.averages[c.key]}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
        <main className="lg:col-span-2">
          <h2 className="font-display font-bold text-xl mb-4">Reviews</h2>
          <ReviewsList reviews={p.reviews} />
        </main>
      </div>
    </div>
  );
}
